import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { BoxProps } from '@chakra-ui/react';
import { API_SERVICE } from '@/pages/AISearch/api/index';
import {
  serializeSearchResults,
  storeLocalSearchHistory,
  getAISummaryMap,
} from '@/pages/AISearch/utils/search';
import { SearchResults } from '@/pages/AISearch/types/search.types';
import { AI_SEARCH_STORAGE_KEYS } from '@/utils/storageKeys';
import { getCompressedItem } from '@/utils/compressedStorage';
import { TAB_ITEMS, TAB_ID, VIEW, ROUTE_MAP } from '../constants';

const POLL_INTERVAL = 2000;
const MAX_POLL_ATTEMPTS = 10;

const ENTITY_TO_TAB_ID: Record<string, string> = {
  contact: 'candidates',
  job: 'jobs',
  interview: 'interviews',
  flow: 'flows',
};

export const useAISearch = () => {
  const history = useHistory();

  // --------------- State ---------------
  const [isLoading, setIsLoading] = useState(false);
  const [selectedTab, setSelectedTab] = useState(TAB_ITEMS[0]);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [summaryInProgress, setSummaryInProgress] = useState(false);
  const [isSearchInitialized, setIsSearchInitialized] = useState(true);
  const [recentlyViewedCounter, setRecentlyViewedCounter] = useState(0);
  const [externalSearchTerm, setExternalSearchTerm] = useState<{
    query: string;
    timestamp: number;
  } | null>(null);

  const [searchResults, setSearchResults] = useState({
    candidates: [] as any[],
    jobs: [] as any[],
    interviews: [] as any[],
    flows: [] as any[],
  });
  const [flows, setFlows] = useState<any[] | null>(null);
  const [flowsIsLoading, setFlowsIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const searchAbortRef = useRef<AbortController | null>(null);

  // --------------- Recent Items (for RecentlyUsed view) ---------------
  const recentSearchQueries = useMemo(
    () =>
      getCompressedItem<string[]>(
        AI_SEARCH_STORAGE_KEYS.RECENT_SEARCH_QUERIES
      ) || [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [recentlyViewedCounter]
  );

  const recentlyViewedCandidates = useMemo(
    () =>
      getCompressedItem<any[]>(
        AI_SEARCH_STORAGE_KEYS.RECENT_VIEWED_CANDIDATES
      ) || [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [recentlyViewedCounter]
  );

  const recentItemsCount =
    recentSearchQueries.length + recentlyViewedCandidates.length;

  // --------------- Derived State ---------------
  const hasRecentSearchValues = useMemo(() => {
    return (
      recentSearchQueries.length > 0 || recentlyViewedCandidates.length > 0
    );
  }, [recentSearchQueries, recentlyViewedCandidates]);

  const currentView = useMemo(() => {
    if (recentlyViewedCounter === 0 && hasRecentSearchValues) {
      return VIEW.RECENTLY_VIEWED;
    }
    if (isSearchInitialized) return VIEW.TRIGGER;
    return VIEW.RESULTS;
  }, [hasRecentSearchValues, isSearchInitialized, recentlyViewedCounter]);

  const currentItems = useMemo(() => {
    if (selectedTab.id === TAB_ID.FLOWS) return flows || [];
    return searchResults?.[selectedTab.id as keyof typeof searchResults] || [];
  }, [selectedTab.id, searchResults, flows]);

  const tabItems = useMemo(() => {
    const countMap: Record<string, number> = {
      candidates: searchResults?.candidates?.length || 0,
      jobs: searchResults?.jobs?.length || 0,
      interviews: searchResults?.interviews?.length || 0,
      flows: flows?.length || 0,
    };
    return TAB_ITEMS.map((item) => ({
      ...item,
      count: countMap[item.id] || 0,
    }));
  }, [searchResults, flows]);

  // --------------- Reset selection on tab/results/view change ---------------
  useEffect(() => {
    setTimeout(() => setSelectedIndex(-1), 0);
  }, [selectedTab.id, searchResults, flows, currentView]);

  // --------------- Keyboard Navigation ---------------
  const getItemRoute = useCallback(
    (item: any) => {
      const routeFn = ROUTE_MAP[selectedTab.id];
      return routeFn ? routeFn(item.id) : '/';
    },
    [selectedTab.id]
  );

  const handleRecentQueryClick = (query: string) => {
    setExternalSearchTerm({ query, timestamp: Date.now() });
  };

  const handleRecentItemEnter = useCallback(
    (index: number) => {
      const queriesCount = recentSearchQueries.length;
      if (index < queriesCount) {
        handleRecentQueryClick(recentSearchQueries[index]);
      } else {
        const candidate = recentlyViewedCandidates[index - queriesCount];
        if (candidate) {
          storeLocalSearchHistory(
            AI_SEARCH_STORAGE_KEYS.RECENT_VIEWED_CANDIDATES,
            candidate
          );
          history.push(`/contacts/${candidate.id}/overview`);
        }
      }
    },
    [recentSearchQueries, recentlyViewedCandidates, history]
  );

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const isResultsView = currentView === VIEW.RESULTS && !isLoading;
      const isRecentView = currentView === VIEW.RECENTLY_VIEWED;

      if (!isResultsView && !isRecentView) return;

      const itemCount = isRecentView ? recentItemsCount : currentItems.length;
      if (itemCount === 0) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((prev) => (prev >= itemCount - 1 ? 0 : prev + 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((prev) => (prev <= 0 ? itemCount - 1 : prev - 1));
      } else if (e.key === 'Enter' && selectedIndex >= 0) {
        if (isRecentView) {
          handleRecentItemEnter(selectedIndex);
        } else {
          const item = currentItems[selectedIndex];
          if (!item) return;
          if (selectedTab.id === TAB_ID.CANDIDATES) {
            storeLocalSearchHistory(
              AI_SEARCH_STORAGE_KEYS.RECENT_VIEWED_CANDIDATES,
              item
            );
          }
          history.push(getItemRoute(item));
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [
    currentView,
    isLoading,
    currentItems,
    recentItemsCount,
    selectedIndex,
    selectedTab.id,
    history,
    getItemRoute,
    handleRecentItemEnter,
  ]);

  // --------------- API Calls ---------------
  const pollForSummaries = async (searchTerm: string, cacheKey: string) => {
    for (let attempt = 0; attempt < MAX_POLL_ATTEMPTS; attempt++) {
      const response = (await API_SERVICE.getAISummaries({
        query: searchTerm,
        cache_key: cacheKey,
        // Hardcoded for now. Ideally, we should implement pagination for search and AI summaries APIs to ensure scalability.
        summary_limit: 50,
      })) as SearchResults;

      if (response?.success) {
        const summaryMap = getAISummaryMap(response);
        setSearchResults((current) => ({
          ...current,
          candidates: current?.candidates?.map((candidate: any) => ({
            ...candidate,
            ai_summary: summaryMap[candidate.rootId] || null,
          })),
        }));
        setSummaryInProgress(false);
        return;
      }

      await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL));
    }
  };

  const fetchSearchResults = async (searchTerm: string) => {
    searchAbortRef.current?.abort();
    const controller = new AbortController();
    searchAbortRef.current = controller;

    setIsSearchInitialized(false);
    setIsLoading(true);
    setHasError(false);

    try {
      const results = (await API_SERVICE.getAISearchResults(
        { query: searchTerm },
        controller.signal
      )) as SearchResults;

      // Auto-select the tab matching the API's entity type on each new search.
      // User's manual tab clicks will override this until the next search.
      // Flows are fetched via a separate API, so the search API won't return
      // entity='flow'. We detect "flow(s)" in the search term to force the Flows tab.
      const entity = results?.entity || 'contact';
      const hasFlowKeyword = /flows?/i.test(searchTerm);
      const tabId = hasFlowKeyword ? 'flows' : ENTITY_TO_TAB_ID[entity];
      if (tabId) {
        const matchingTab = TAB_ITEMS.find((t) => t.id === tabId);
        if (matchingTab) setSelectedTab(matchingTab);
      }

      setSearchResults(serializeSearchResults(results) || ({} as any));

      if (results?.cache_key) {
        setSummaryInProgress(true);
        pollForSummaries(searchTerm, results.cache_key);
      }
    } catch (error: any) {
      if (error?.name === 'CanceledError' || controller.signal.aborted) return;
      setHasError(true);
    } finally {
      if (!controller.signal.aborted) setIsLoading(false);
    }
  };

  const fetchFlows = async (searchTerm: string) => {
    setFlowsIsLoading(true);
    const response = await API_SERVICE.getFlows({ q: searchTerm });
    setFlows(response?.outreaches || []);
    setFlowsIsLoading(false);
  };

  const handleSearch = (searchTerm: string) => {
    fetchSearchResults(searchTerm);
    fetchFlows(searchTerm);
    setRecentlyViewedCounter(1);
    storeLocalSearchHistory(
      AI_SEARCH_STORAGE_KEYS.RECENT_SEARCH_QUERIES,
      searchTerm
    );
  };

  const cancelSearch = () => {
    console.log('cancelSearch');
    searchAbortRef.current?.abort();
    searchAbortRef.current = null;
    setIsLoading(false);
  };

  const handleClose = () => {
    setIsSearchInitialized(false);
    setRecentlyViewedCounter(0);
  };

  const searchResultsContainerProps = useMemo<BoxProps>(() => {
    const showRecentlyViewed = currentView === VIEW.RECENTLY_VIEWED;
    const searchWithoutHistory = currentView === VIEW.TRIGGER;
    return {
      bg: '#fff',
      className: 'ai-search-results-container',
      h: '100%',
      w: '100%',
      borderColor: 'secondary-100',
      borderRadius: '12px',
      p: '20px 20px 0px 20px',
      pt: '0px',
      mt: '10px',
      minH: showRecentlyViewed || searchWithoutHistory ? 'auto' : '500px',
      maxH: '600px',
      overflowY: 'auto',
      direction: 'column',
    };
  }, [currentView]);

  // --------------- Public API ---------------
  return {
    currentView,
    searchResultsContainerProps,
    isLoading,

    handleSearch,
    cancelSearch,
    externalSearchTerm,
    handleRecentQueryClick,
    handleClose,

    tabItems,
    selectedTab,
    setSelectedTab,

    searchResults,
    flows,
    flowsIsLoading,
    summaryInProgress,
    selectedIndex,

    recentSearchQueries,
    recentlyViewedCandidates,
    hasError,
  };
};
